/**
 * Audiovisual - Página pública para mostrar videos de YouTube
 *
 * Layout: título (h1) → descripción → video iframe
 * Centrado, mobile first, con header/footer coherente con BaseTemplate
 * Los videos se cargan desde la API /api/audiovisual
 */

import { SiteLayout } from "@/components/SiteLayout";
import { useSEO } from "@/hooks/useSEO";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { cn } from "@/lib/utils";
import { Youtube, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface AudiovisualItem {
  id: string;
  title: string;
  description: string;
  youtubeUrl: string;
  order: number;
}

// Extraer video ID de URL de YouTube o iframe embed
function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
}

const Audiovisual = () => {
  const { t } = useTranslation();
  useSEO("audiovisual");
  const { isBaseTemplateBlack: isDarkShell } = useTemplateSelection();
  const [items, setItems] = useState<AudiovisualItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      setIsLoading(true);
      const response = await fetch("/api/audiovisual", { cache: "no-store" });
      if (!response.ok) throw new Error("Failed to load audiovisual");

      const data = await response.json();
      setItems(data.items || []);
    } catch (error) {
      console.error("Error loading audiovisual:", error);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <SiteLayout>
        <div className="flex-1 flex items-center justify-center py-20">
          <Loader2
            className={cn(
              "w-8 h-8 animate-spin",
              isDarkShell ? "text-white/50" : "text-black/50",
            )}
          />
        </div>
      </SiteLayout>
    );
  }

  if (items.length === 0) {
    return (
      <SiteLayout>
        <div className="flex-1 flex flex-col items-center justify-center px-5 py-24 min-h-[50vh]">
          <Youtube
            className={cn(
              "w-16 h-16 mb-4",
              isDarkShell ? "text-white/40" : "text-black/40",
            )}
          />
          <h1
            className={cn(
              "text-2xl sm:text-3xl md:text-4xl font-display font-light tracking-tight uppercase mb-4",
              isDarkShell ? "text-white" : "text-black",
            )}
          >
            {t("audiovisual.title")}
          </h1>
          <p
            className={cn(
              "font-body text-sm text-center max-w-sm mb-10 leading-relaxed",
              isDarkShell ? "text-white/60" : "text-black/60",
            )}
          >
            {t("audiovisual.emptyComingSoon")}
          </p>
          <a
            href="/"
            className={cn(
              "font-body text-xs uppercase tracking-[0.2em] transition-colors font-light",
              isDarkShell
                ? "text-white/60 hover:text-white"
                : "text-black/60 hover:text-black",
            )}
          >
            {t("audiovisual.backHomeArrow")}
          </a>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="w-full max-w-2xl mx-auto px-4 py-8 sm:py-12 md:py-16">
        {/* Page title */}
        <header className="text-center mb-10 sm:mb-14">
          <h1
            className={cn(
              "text-2xl sm:text-3xl md:text-4xl font-display font-light tracking-tight uppercase",
              isDarkShell ? "text-white" : "text-black",
            )}
          >
            {t("audiovisual.title")}
          </h1>
        </header>

        {/* Videos: título → descripción → video, centrado */}
        <div className="flex flex-col items-center gap-12 sm:gap-16">
          {items.map((item) => {
            const videoId = extractYouTubeId(item.youtubeUrl);
            if (!videoId) return null;

            return (
              <article
                key={item.id}
                className="font-body w-full flex flex-col items-center text-center"
              >
                {/* Título */}
                <h2
                  className={cn(
                    "text-xl sm:text-2xl font-display font-light tracking-tight uppercase mb-3 sm:mb-4",
                    isDarkShell ? "text-white" : "text-black",
                  )}
                >
                  {item.title}
                </h2>

                {/* Descripción */}
                {item.description && (
                  <p
                    className={cn(
                      "text-sm sm:text-base leading-relaxed mb-4 sm:mb-6 max-w-xl",
                      isDarkShell ? "text-white/70" : "text-black/70",
                    )}
                  >
                    {item.description}
                  </p>
                )}

                {/* Video iframe 16:9, responsive, centrado */}
                <div className="w-full max-w-[560px] aspect-video rounded-lg overflow-hidden bg-black shadow-lg">
                  <iframe
                    width="560"
                    height="315"
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title={item.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              </article>
            );
          })}
        </div>

        {/* Volver al inicio */}
        <div className="text-center mt-12 sm:mt-16">
          <a
            href="/"
            className={cn(
              "font-body inline-block text-xs uppercase tracking-[0.2em] transition-colors font-light",
              isDarkShell
                ? "text-white/60 hover:text-white"
                : "text-black/60 hover:text-black",
            )}
          >
            {t("audiovisual.backHome")}
          </a>
        </div>
      </div>
    </SiteLayout>
  );
};

export default Audiovisual;
